/**
 * F09 v2 — request decision dialogs (Reject + More-info) and the entity /
 * candidate / watchlist action dialogs (Revoke, reason-only, Invite, Dismiss,
 * Resolve). Each validates client-side against the frozen Zod bounds before
 * calling the API; server errors render in the shared error banner.
 */
import { useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import {
  Checkbox,
  FormField,
  SegmentedControl,
  Select,
  TextInput,
  Textarea,
} from '@bravemove/ui-web';
import {
  ADMIN_REVOCATION_CATEGORIES,
  VERIFICATION_INTERNAL_NOTE_MAX,
  VERIFICATION_INTERNAL_NOTE_MIN,
  VERIFICATION_INVITE_MESSAGE_MAX,
  VERIFICATION_MORE_INFO_DUE_DAYS,
  VERIFICATION_MORE_INFO_DUE_DAYS_MAX,
  VERIFICATION_MORE_INFO_DUE_DAYS_MIN,
  VERIFICATION_MORE_INFO_PROMPT_MAX,
  VERIFICATION_MORE_INFO_PROMPT_MIN,
  VERIFICATION_NOTE_MAX,
  VERIFICATION_REASON_CATEGORIES,
  VERIFICATION_REASON_MAX,
  VERIFICATION_REASON_MIN,
  VERIFICATION_RESURFACE_AFTER_DAYS_MAX,
  VERIFICATION_RESURFACE_AFTER_DAYS_MIN,
  VERIFICATION_REVOCATION_REASON_MAX,
  allowedDocumentKinds,
  type AdminRevocationCategory,
  type VerifiableEntityType,
  type VerificationDocumentKind,
  type VerificationReasonCategory,
  type WatchlistResolution,
} from '@bravemove/types';
import { adminVerificationApi } from '../../api/verification';
import { FormModal } from './FormModal';
import { intRangeError, lengthError, noErrors } from './validation';
import { docKindLabel, rejectionLabel, revocationLabel } from './labels';

// ─── Reject ──────────────────────────────────────────────────────────────────

export function RejectDialog({
  requestId,
  onClose,
  onDone,
}: {
  readonly requestId: string;
  readonly onClose: () => void;
  readonly onDone: () => void;
}) {
  const { t } = useI18n();
  const [category, setCategory] = useState<VerificationReasonCategory | ''>('');
  const [note, setNote] = useState('');
  const [internalNote, setInternalNote] = useState('');
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [submitError, setSubmitError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const next = {
      category: category === '' ? t('admin:verification.validation.required') : undefined,
      note: lengthError(t, note, 0, VERIFICATION_NOTE_MAX, false),
      internalNote: lengthError(
        t,
        internalNote,
        VERIFICATION_INTERNAL_NOTE_MIN,
        VERIFICATION_INTERNAL_NOTE_MAX,
        false,
      ),
    };
    setErrors(next);
    if (!noErrors(next) || category === '') return;
    setBusy(true);
    setSubmitError(null);
    try {
      await adminVerificationApi.reject(requestId, {
        reasonCategory: category,
        ...(note.trim() ? { note: note.trim() } : {}),
        ...(internalNote.trim() ? { internalNote: internalNote.trim() } : {}),
      });
      onDone();
    } catch (err) {
      setSubmitError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <FormModal
      title={t('admin:verification.reject.title')}
      onClose={onClose}
      onConfirm={() => void submit()}
      confirmLabel={t('admin:verification.reject.confirm')}
      destructive
      busy={busy}
      error={submitError}
      testId="reject-dialog"
    >
      <FormField label={t('admin:verification.reject.category')} error={errors.category} required>
        <Select
          value={category}
          placeholder={t('admin:verification.reject.categoryPlaceholder')}
          options={VERIFICATION_REASON_CATEGORIES.map((c) => ({
            value: c,
            label: rejectionLabel(t, c),
          }))}
          invalid={!!errors.category}
          data-testid="reject-dialog-category"
          onChange={(e) => {
            const v = e.target.value;
            setCategory(
              (VERIFICATION_REASON_CATEGORIES as readonly string[]).includes(v)
                ? (v as VerificationReasonCategory)
                : '',
            );
          }}
        />
      </FormField>
      <FormField
        label={t('admin:verification.reject.note')}
        helper={t('admin:verification.reject.noteHelper')}
        error={errors.note}
      >
        <Textarea
          value={note}
          maxLength={VERIFICATION_NOTE_MAX}
          invalid={!!errors.note}
          data-testid="reject-dialog-note"
          onChange={(e) => setNote(e.target.value)}
        />
      </FormField>
      <FormField
        label={t('admin:verification.reject.internalNote')}
        helper={t('admin:verification.reject.internalNoteHelper')}
        error={errors.internalNote}
      >
        <Textarea
          value={internalNote}
          maxLength={VERIFICATION_INTERNAL_NOTE_MAX}
          invalid={!!errors.internalNote}
          data-testid="reject-dialog-internal-note"
          onChange={(e) => setInternalNote(e.target.value)}
        />
      </FormField>
      <section
        className="rounded-md border border-hairline bg-surface-2 px-3 py-2"
        data-testid="reject-dialog-preview"
      >
        <h3 className="font-mono text-[11px] font-bold uppercase tracking-wider text-ink-3">
          {t('admin:verification.reject.preview')}
        </h3>
        {category === '' ? (
          <p className="mt-1 text-[13px] text-ink-3">
            {t('admin:verification.reject.previewEmpty')}
          </p>
        ) : (
          <div className="mt-1 space-y-1 text-[13px] text-ink">
            <p className="font-semibold">{rejectionLabel(t, category)}</p>
            {note.trim() ? <p className="whitespace-pre-wrap text-ink-2">{note.trim()}</p> : null}
          </div>
        )}
      </section>
    </FormModal>
  );
}

// ─── More info ───────────────────────────────────────────────────────────────

export function MoreInfoDialog({
  requestId,
  entityType,
  onClose,
  onDone,
}: {
  readonly requestId: string;
  readonly entityType: VerifiableEntityType;
  readonly onClose: () => void;
  readonly onDone: () => void;
}) {
  const { t } = useI18n();
  const [prompt, setPrompt] = useState('');
  const [kinds, setKinds] = useState<readonly VerificationDocumentKind[]>([]);
  const [dueInDays, setDueInDays] = useState(String(VERIFICATION_MORE_INFO_DUE_DAYS));
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [submitError, setSubmitError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const toggleKind = (kind: VerificationDocumentKind, on: boolean) =>
    setKinds((prev) => (on ? [...prev, kind] : prev.filter((k) => k !== kind)));

  const submit = async () => {
    const next = {
      prompt: lengthError(
        t,
        prompt,
        VERIFICATION_MORE_INFO_PROMPT_MIN,
        VERIFICATION_MORE_INFO_PROMPT_MAX,
        true,
      ),
      dueInDays: intRangeError(
        t,
        dueInDays,
        VERIFICATION_MORE_INFO_DUE_DAYS_MIN,
        VERIFICATION_MORE_INFO_DUE_DAYS_MAX,
        false,
      ),
    };
    setErrors(next);
    if (!noErrors(next)) return;
    setBusy(true);
    setSubmitError(null);
    try {
      await adminVerificationApi.requestMoreInfo(requestId, {
        prompt: prompt.trim(),
        ...(kinds.length > 0 ? { requestedDocumentKinds: [...kinds] } : {}),
        ...(dueInDays.trim() ? { dueInDays: Number(dueInDays.trim()) } : {}),
      });
      onDone();
    } catch (err) {
      setSubmitError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <FormModal
      title={t('admin:verification.moreInfo.title')}
      onClose={onClose}
      onConfirm={() => void submit()}
      confirmLabel={t('admin:verification.moreInfo.confirm')}
      busy={busy}
      error={submitError}
      testId="more-info-dialog"
    >
      <FormField
        label={t('admin:verification.moreInfo.prompt')}
        helper={t('admin:verification.moreInfo.promptHelper')}
        error={errors.prompt}
        required
      >
        <Textarea
          value={prompt}
          maxLength={VERIFICATION_MORE_INFO_PROMPT_MAX}
          invalid={!!errors.prompt}
          data-testid="more-info-dialog-prompt"
          onChange={(e) => setPrompt(e.target.value)}
        />
      </FormField>
      <fieldset className="space-y-1.5">
        <legend className="text-[12px] font-semibold text-ink-2">
          {t('admin:verification.moreInfo.kinds')}
        </legend>
        <div className="grid gap-1.5 sm:grid-cols-2">
          {allowedDocumentKinds(entityType).map((kind) => (
            <Checkbox
              key={kind}
              checked={kinds.includes(kind)}
              data-testid={`more-info-dialog-kind-${kind}`}
              onChange={(e) => toggleKind(kind, e.target.checked)}
              label={docKindLabel(t, kind)}
            />
          ))}
        </div>
      </fieldset>
      <FormField
        label={t('admin:verification.moreInfo.dueInDays')}
        helper={t('admin:verification.moreInfo.dueInDaysHelper')}
        error={errors.dueInDays}
      >
        <TextInput
          type="number"
          inputMode="numeric"
          min={VERIFICATION_MORE_INFO_DUE_DAYS_MIN}
          max={VERIFICATION_MORE_INFO_DUE_DAYS_MAX}
          value={dueInDays}
          invalid={!!errors.dueInDays}
          data-testid="more-info-dialog-due"
          onChange={(e) => setDueInDays(e.target.value)}
        />
      </FormField>
    </FormModal>
  );
}

// ─── Revoke ──────────────────────────────────────────────────────────────────

export function RevokeDialog({
  entityType,
  entityId,
  entityName,
  onClose,
  onDone,
}: {
  readonly entityType: VerifiableEntityType;
  readonly entityId: string;
  readonly entityName: string;
  readonly onClose: () => void;
  readonly onDone: () => void;
}) {
  const { t } = useI18n();
  const phrase = t('admin:verification.revoke.phrase');
  const [category, setCategory] = useState<AdminRevocationCategory | ''>('');
  const [reason, setReason] = useState('');
  const [internalNote, setInternalNote] = useState('');
  const [typed, setTyped] = useState('');
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [submitError, setSubmitError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const next = {
      category: category === '' ? t('admin:verification.validation.required') : undefined,
      reason: lengthError(
        t,
        reason,
        VERIFICATION_REASON_MIN,
        VERIFICATION_REVOCATION_REASON_MAX,
        true,
      ),
      internalNote: lengthError(
        t,
        internalNote,
        VERIFICATION_INTERNAL_NOTE_MIN,
        VERIFICATION_INTERNAL_NOTE_MAX,
        false,
      ),
      typed: typed.trim() === phrase ? undefined : t('admin:verification.validation.phrase'),
    };
    setErrors(next);
    if (!noErrors(next) || category === '') return;
    setBusy(true);
    setSubmitError(null);
    try {
      await adminVerificationApi.revoke(entityType, entityId, {
        reasonCategory: category,
        reason: reason.trim(),
        ...(internalNote.trim() ? { internalNote: internalNote.trim() } : {}),
      });
      onDone();
    } catch (err) {
      setSubmitError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <FormModal
      title={t('admin:verification.revoke.title')}
      description={t('admin:verification.revoke.description', { entity: entityName })}
      onClose={onClose}
      onConfirm={() => void submit()}
      confirmLabel={t('admin:verification.revoke.confirm')}
      destructive
      busy={busy}
      error={submitError}
      testId="revoke-dialog"
    >
      <FormField label={t('admin:verification.revoke.category')} error={errors.category} required>
        <Select
          value={category}
          placeholder={t('admin:verification.revoke.categoryPlaceholder')}
          options={ADMIN_REVOCATION_CATEGORIES.map((c) => ({
            value: c,
            label: revocationLabel(t, c),
          }))}
          invalid={!!errors.category}
          data-testid="revoke-dialog-category"
          onChange={(e) => {
            const v = e.target.value;
            setCategory(
              (ADMIN_REVOCATION_CATEGORIES as readonly string[]).includes(v)
                ? (v as AdminRevocationCategory)
                : '',
            );
          }}
        />
      </FormField>
      <FormField
        label={t('admin:verification.revoke.reason')}
        helper={t('admin:verification.revoke.reasonHelper')}
        error={errors.reason}
        required
      >
        <Textarea
          value={reason}
          maxLength={VERIFICATION_REVOCATION_REASON_MAX}
          invalid={!!errors.reason}
          data-testid="revoke-dialog-reason"
          onChange={(e) => setReason(e.target.value)}
        />
      </FormField>
      <FormField
        label={t('admin:verification.revoke.internalNote')}
        helper={t('admin:verification.revoke.internalNoteHelper')}
        error={errors.internalNote}
      >
        <Textarea
          value={internalNote}
          maxLength={VERIFICATION_INTERNAL_NOTE_MAX}
          invalid={!!errors.internalNote}
          data-testid="revoke-dialog-internal-note"
          onChange={(e) => setInternalNote(e.target.value)}
        />
      </FormField>
      <FormField
        label={t('admin:verification.revoke.confirmPhrase', { phrase })}
        error={errors.typed}
        required
      >
        <TextInput
          value={typed}
          autoComplete="off"
          invalid={!!errors.typed}
          data-testid="revoke-dialog-phrase"
          onChange={(e) => setTyped(e.target.value)}
        />
      </FormField>
    </FormModal>
  );
}

// ─── Reason-only (lift cooldown / unlock re-request) ─────────────────────────

export function ReasonDialog({
  title,
  description,
  confirmLabel,
  testId,
  onSubmit,
  onClose,
  onDone,
}: {
  readonly title: string;
  readonly description: string;
  readonly confirmLabel: string;
  readonly testId: string;
  readonly onSubmit: (reason: string) => Promise<unknown>;
  readonly onClose: () => void;
  readonly onDone: () => void;
}) {
  const { t } = useI18n();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);
  const [submitError, setSubmitError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const e = lengthError(t, reason, VERIFICATION_REASON_MIN, VERIFICATION_REASON_MAX, true);
    setError(e);
    if (e) return;
    setBusy(true);
    setSubmitError(null);
    try {
      await onSubmit(reason.trim());
      onDone();
    } catch (err) {
      setSubmitError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <FormModal
      title={title}
      description={description}
      onClose={onClose}
      onConfirm={() => void submit()}
      confirmLabel={confirmLabel}
      busy={busy}
      error={submitError}
      testId={testId}
    >
      <FormField
        label={t('admin:verification.reasonField.label')}
        helper={t('admin:verification.reasonField.helper')}
        error={error}
        required
      >
        <Textarea
          value={reason}
          maxLength={VERIFICATION_REASON_MAX}
          invalid={!!error}
          data-testid={`${testId}-reason`}
          onChange={(e) => setReason(e.target.value)}
        />
      </FormField>
    </FormModal>
  );
}

// ─── Candidates ──────────────────────────────────────────────────────────────

export function InviteCandidateDialog({
  candidateId,
  entityName,
  onClose,
  onDone,
  onError,
}: {
  readonly candidateId: string;
  readonly entityName: string;
  readonly onClose: () => void;
  readonly onDone: () => void;
  /** Called instead of the inline banner for errors the page handles (e.g. CANDIDATES_DISABLED). */
  readonly onError?: (err: unknown) => boolean;
}) {
  const { t } = useI18n();
  const [message, setMessage] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);
  const [submitError, setSubmitError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const e = lengthError(t, message, 0, VERIFICATION_INVITE_MESSAGE_MAX, false);
    setError(e);
    if (e) return;
    setBusy(true);
    setSubmitError(null);
    try {
      await adminVerificationApi.inviteCandidate(
        candidateId,
        message.trim() ? { message: message.trim() } : {},
      );
      onDone();
    } catch (err) {
      if (!onError?.(err)) setSubmitError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <FormModal
      title={t('admin:verification.candidates.inviteTitle')}
      description={t('admin:verification.candidates.inviteDescription', { entity: entityName })}
      onClose={onClose}
      onConfirm={() => void submit()}
      confirmLabel={t('admin:verification.candidates.inviteConfirm')}
      busy={busy}
      error={submitError}
      testId="invite-dialog"
    >
      <FormField
        label={t('admin:verification.candidates.inviteMessage')}
        helper={t('admin:verification.candidates.inviteMessageHelper')}
        error={error}
      >
        <Textarea
          value={message}
          maxLength={VERIFICATION_INVITE_MESSAGE_MAX}
          invalid={!!error}
          data-testid="invite-dialog-message"
          onChange={(e) => setMessage(e.target.value)}
        />
      </FormField>
    </FormModal>
  );
}

export function DismissCandidateDialog({
  candidateId,
  onClose,
  onDone,
}: {
  readonly candidateId: string;
  readonly onClose: () => void;
  readonly onDone: () => void;
}) {
  const { t } = useI18n();
  const [reason, setReason] = useState('');
  const [days, setDays] = useState('');
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [submitError, setSubmitError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const next = {
      reason: lengthError(t, reason, VERIFICATION_REASON_MIN, VERIFICATION_REASON_MAX, true),
      days: intRangeError(
        t,
        days,
        VERIFICATION_RESURFACE_AFTER_DAYS_MIN,
        VERIFICATION_RESURFACE_AFTER_DAYS_MAX,
        false,
      ),
    };
    setErrors(next);
    if (!noErrors(next)) return;
    setBusy(true);
    setSubmitError(null);
    try {
      await adminVerificationApi.dismissCandidate(candidateId, {
        reason: reason.trim(),
        ...(days.trim() ? { resurfaceAfterDays: Number(days.trim()) } : {}),
      });
      onDone();
    } catch (err) {
      setSubmitError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <FormModal
      title={t('admin:verification.candidates.dismissTitle')}
      onClose={onClose}
      onConfirm={() => void submit()}
      confirmLabel={t('admin:verification.candidates.dismissConfirm')}
      busy={busy}
      error={submitError}
      testId="dismiss-dialog"
    >
      <FormField
        label={t('admin:verification.candidates.dismissReason')}
        helper={t('admin:verification.candidates.dismissReasonHelper')}
        error={errors.reason}
        required
      >
        <Textarea
          value={reason}
          maxLength={VERIFICATION_REASON_MAX}
          invalid={!!errors.reason}
          data-testid="dismiss-dialog-reason"
          onChange={(e) => setReason(e.target.value)}
        />
      </FormField>
      <FormField
        label={t('admin:verification.candidates.resurfaceDays')}
        helper={t('admin:verification.candidates.resurfaceHelper')}
        error={errors.days}
      >
        <TextInput
          type="number"
          inputMode="numeric"
          min={VERIFICATION_RESURFACE_AFTER_DAYS_MIN}
          max={VERIFICATION_RESURFACE_AFTER_DAYS_MAX}
          value={days}
          invalid={!!errors.days}
          data-testid="dismiss-dialog-days"
          onChange={(e) => setDays(e.target.value)}
        />
      </FormField>
    </FormModal>
  );
}

// ─── Watchlist ───────────────────────────────────────────────────────────────

export function ResolveWatchlistDialog({
  entryId,
  onClose,
  onDone,
}: {
  readonly entryId: string;
  readonly onClose: () => void;
  readonly onDone: () => void;
}) {
  const { t } = useI18n();
  const [resolution, setResolution] = useState<WatchlistResolution>('resolved');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);
  const [submitError, setSubmitError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const e = lengthError(t, note, VERIFICATION_REASON_MIN, VERIFICATION_REASON_MAX, true);
    setError(e);
    if (e) return;
    setBusy(true);
    setSubmitError(null);
    try {
      await adminVerificationApi.resolveWatchlist(entryId, { resolution, note: note.trim() });
      onDone();
    } catch (err) {
      setSubmitError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <FormModal
      title={t('admin:verification.watchlist.resolveTitle')}
      onClose={onClose}
      onConfirm={() => void submit()}
      confirmLabel={t('admin:verification.watchlist.resolveConfirm')}
      busy={busy}
      error={submitError}
      testId="resolve-dialog"
    >
      <FormField label={t('admin:verification.watchlist.resolution')}>
        <SegmentedControl<WatchlistResolution>
          value={resolution}
          onChange={setResolution}
          aria-label={t('admin:verification.watchlist.resolution')}
          options={[
            { value: 'resolved', label: t('admin:verification.watchlist.resolutionResolved') },
            { value: 'dismissed', label: t('admin:verification.watchlist.resolutionDismissed') },
          ]}
        />
      </FormField>
      <FormField
        label={t('admin:verification.watchlist.note')}
        helper={t('admin:verification.watchlist.noteHelper')}
        error={error}
        required
      >
        <Textarea
          value={note}
          maxLength={VERIFICATION_REASON_MAX}
          invalid={!!error}
          data-testid="resolve-dialog-note"
          onChange={(e) => setNote(e.target.value)}
        />
      </FormField>
    </FormModal>
  );
}
